<template>
  <div class="tree-item">
    <div
      class="tree-node"
      :class="{ 'inactive': !department.is_active }"
    >
      <!-- Expand/Collapse Button -->
      <b-button
        v-if="hasChildren"
        variant="flat"
        size="sm"
        class="expand-btn p-0 mr-50"
        @click="$emit('toggle', department.id)"
      >
        <feather-icon
          :icon="isExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
          size="16"
        />
      </b-button>
      <span
        v-else
        class="expand-spacer"
      />

      <!-- Department Icon -->
      <feather-icon
        :icon="department.is_active ? 'FolderIcon' : 'FolderMinusIcon'"
        :class="department.is_active ? 'text-primary' : 'text-muted'"
        size="18"
        class="mr-50"
      />

      <!-- Department Info -->
      <div class="department-info flex-grow-1">
        <div class="d-flex align-items-center">
          <strong class="department-name mr-1">{{ department.name }}</strong>
          <b-badge
            variant="light-secondary"
            class="text-monospace mr-50"
          >
            {{ department.code }}
          </b-badge>
          <b-badge
            v-if="!department.is_active"
            variant="light-secondary"
            class="mr-50"
          >
            Inactive
          </b-badge>
        </div>
        <div class="department-meta text-muted">
          <small>
            <feather-icon
              icon="UserIcon"
              size="12"
              class="mr-25"
            />
            <span v-if="department.manager">
              {{ department.manager.full_name }}
            </span>
            <span
              v-else
              class="text-muted"
            >No Manager</span>

            <span class="mx-50">|</span>

            <feather-icon
              icon="UsersIcon"
              size="12"
              class="mr-25"
            />
            {{ department.active_employees_count || 0 }}/{{ department.employees_count || 0 }} employees

            <span
              v-if="hasChildren"
              class="mx-50"
            >|</span>

            <template v-if="hasChildren">
              <feather-icon
                icon="GitBranchIcon"
                size="12"
                class="mr-25"
              />
              {{ department.children.length }} sub-departments
            </template>
          </small>
        </div>
      </div>

      <!-- Actions -->
      <div class="tree-actions">
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="flat-primary"
          size="sm"
          class="btn-icon"
          @click="$emit('view', department.id)"
        >
          <feather-icon
            icon="EyeIcon"
            size="16"
          />
        </b-button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="flat-primary"
          size="sm"
          class="btn-icon"
          @click="$emit('edit', department)"
        >
          <feather-icon
            icon="EditIcon"
            size="16"
          />
        </b-button>
        <b-button
          v-ripple.400="'rgba(40, 199, 111, 0.15)'"
          variant="flat-success"
          size="sm"
          class="btn-icon"
          @click="$emit('add-child', department)"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
          />
        </b-button>
        <b-button
          v-ripple.400="'rgba(234, 84, 85, 0.15)'"
          variant="flat-danger"
          size="sm"
          class="btn-icon"
          @click="$emit('delete', department)"
        >
          <feather-icon
            icon="TrashIcon"
            size="16"
          />
        </b-button>
      </div>
    </div>

    <!-- Children -->
    <transition name="slide-fade">
      <div
        v-if="hasChildren && isExpanded"
        class="tree-children"
      >
        <tree-item
          v-for="child in department.children"
          :key="child.id"
          :department="child"
          :expanded-ids="expandedIds"
          @toggle="$emit('toggle', $event)"
          @view="$emit('view', $event)"
          @edit="$emit('edit', $event)"
          @add-child="$emit('add-child', $event)"
          @delete="$emit('delete', $event)"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import {
  BButton,
  BBadge,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'TreeItem',

  components: {
    BButton,
    BBadge,
  },

  directives: {
    Ripple,
  },

  props: {
    department: {
      type: Object,
      required: true,
    },
    expandedIds: {
      type: Set,
      default: () => new Set(),
    },
  },

  computed: {
    hasChildren() {
      return this.department.children && this.department.children.length > 0
    },
    isExpanded() {
      return this.expandedIds.has(this.department.id)
    },
  },
}
</script>

<style lang="scss" scoped>
.tree-item {
  margin-bottom: 0.5rem;
}

.tree-node {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 0.357rem;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    transform: translateY(-2px);

    .tree-actions {
      opacity: 1;
    }
  }

  &.inactive {
    opacity: 0.6;
    background-color: #f8f9fa;
  }
}

.expand-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.expand-spacer {
  width: 24px;
  margin-right: 0.5rem;
  display: inline-block;
}

.department-info {
  flex: 1;
  min-width: 0;
}

.department-name {
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.department-meta {
  font-size: 0.857rem;
  margin-top: 0.25rem;
}

.tree-actions {
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.tree-children {
  margin-left: 2rem;
  margin-top: 0.5rem;
  padding-left: 1rem;
  border-left: 2px solid #ebe9f1;
}

// Slide fade animation
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
